import { useContext, useEffect, useRef, useState } from "react";
import IDeleteFileUploadDTO from "../../../models/DTO/IDeleteFileUploadDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IAttachment from "../../../models/IAttachment";
import OwLabel from "../OwLabel/OwLabel";
import OwTranslate from "../OwTranslate/OwTranslate";
import UserContext from "../../../contexts/UserContext";
import VendorContext from "../../../contexts/VendorContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { SocketContext } from "../../../contexts/SocketContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IOnGetAttachmentSignedUrlDTO from "../../../models/DTO/IOnGetAttachmentSignedUrlDTO";
import IGetAttachmentSignedUrlDTO from "../../../models/DTO/IGetAttachmentSignedUrlDTO";
import { ConfirmDeleteOverlayFn } from "../ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import OwButton from "../OwButton/OwButton";

type Props = {
    leftIconName?: string;
    title?: string;
    attachment: IAttachment;
    isReadOnly?: boolean;
    noTopOffset?: boolean;
    noFilename?: boolean;
    className?: string;
};

export const OwS3DownloadLink = (props: Props) => {
    const userModel = useContext(UserContext);
    const vendorModel = useContext(VendorContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const attachmentItemRef = useRef<HTMLDivElement | null>(null);
    const attachOverlayRef = useRef<any>(null);

    const id = `attachment-${props.attachment.guid}`;
    const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
    const [retrievingFile, setRetrievingFile] = useState<boolean>(false);
    const [targetElem, setTargetElem] = useState<string | null>(null);

    const onGetFileAttachmentSignedUrl = (data: IOnGetAttachmentSignedUrlDTO) => {
        if (data.attachmentGuid === props.attachment.guid) {
            const link = document.createElement("a");
            link.href = data.signedUrl;
            link.download = props.attachment.name;
            link.style.display = "none";

            document.body.appendChild(link); // Append link to body
            link.click(); // Programmatically click the link
            document.body.removeChild(link);
        }
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.FileUploadOnGetAttachmentSignedUrl, fn: onGetFileAttachmentSignedUrl },
        // { endpoint: EndpointsEnum.FileUploadOnDeleteFileUpload, fn: onDeleteFileUpload },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        if (retrievingFile) {
            // downloadAttachment(props.attachment.guid);
            getAttachmentSignedUrl();
        }
    }, [retrievingFile]);

    const getAttachmentSignedUrl = () => {
        if (props.attachment) {
            const payload: IGetAttachmentSignedUrlDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                attachmentGuid: props.attachment.guid,
            };

            socket.emit(EndpointsEnum.FileUploadGetAttachmentSignedUrl, payload);
        }
    };

    const cancelConfirmDelete = () => {
        setShowConfirmDelete(false);
        setTargetElem(null);

        // setState({
        //     attachmentItemRef: null,
        //     showConfirmDelete: false,
        // });
    };

    const showConfirmDeleteFn = () => {
        setShowConfirmDelete(true);
    };

    const getDeleteConfirmOverlay = () => {
        return (
            <div className={"position-absolute w-100 h-100 d-flex align-items-center ps-2 overlayRow"}>
                <span className={"me-2"}>
                    <OwTranslate defaultMessage={"confirm_delete"} id={"confirm_delete"} />?{" "}
                </span>
                <div className="btn-group d-inline-flex">
                    <button
                        type="button"
                        className="btn btn-danger btn-sm ms-2"
                        onClick={(e: any) => {
                            deleteAttachment();
                        }}
                        title="Confirm Delete">
                        <i className="far fa-trash-alt" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={(e: any) => {
                            cancelConfirmDelete();
                        }}
                        title="Cancel">
                        <i className="far fa-times" />
                    </button>
                </div>
            </div>
        );
    };

    const getActions = () => {
        let deleteAction: any = null;

        if (!props.isReadOnly) {
            deleteAction = (
                <i
                    className={"fa fa-trash-alt me-4 pointer"}
                    onClick={(e: any) => {
                        showConfirmDeleteFn();
                    }}
                />
            );
        }

        return (
            <span className={"actions"}>
                <i
                    className={"fa fa-save ms-2 me-4 pointer"}
                    onClick={(e: any) => {
                        setRetrievingFile(true);
                    }}
                />

                {deleteAction}
            </span>
        );
    };

    // const downloadAttachment = (attachmentGuid: string) => {
    //     const payload: IGetFileUploadDTO = {
    //         userGuid: userModel.guid,
    //         userToken: userModel.token,
    //         supplierGuid: supplierModel.guid,
    //         attachmentGuid,
    //     };
    //
    //     if (vendorModel && !userModel.isSupplierUser) {
    //         payload.vendorGuid = vendorModel.guid;
    //     }
    //
    //     socket.emit(EndpointsEnum.FileUploadGetFileUpload, payload);
    //
    //     // setRetrievingFile(true);
    // };

    const deleteAttachment = () => {
        const payload: IDeleteFileUploadDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            attachmentGuid: props.attachment.guid,
        };

        socket.emit(EndpointsEnum.FileUploadDeleteFileUpload, payload);
    };

    const setSourceElement = () => {
        setTargetElem(id);
    };

    // Function to get appropriate file icon based on extension
    const getFileIcon = () => {
        if (!props.attachment.name) return "fas fa-file";

        const extension = props.attachment.name.split('.').pop()?.toLowerCase() || '';

        switch(extension) {
            case 'pdf':
                return "fas fa-file-pdf";
            case 'doc':
            case 'docx':
                return "fas fa-file-word";
            case 'xls':
            case 'xlsx':
                return "fas fa-file-excel";
            case 'ppt':
            case 'pptx':
                return "fas fa-file-powerpoint";
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                return "fas fa-file-image";
            case 'zip':
            case 'rar':
                return "fas fa-file-archive";
            default:
                return "fas fa-file-alt";
        }
    };

    const fileIcon = getFileIcon();

    const leftIcon = props.leftIconName ? <i className={props.leftIconName}></i> : <i className={fileIcon}></i>;

    let title: any = null;

    if (props.title) {
        title = (
            <span>
                <OwLabel className={"pointer"} title={props.title} noBottomMargin={true} />
                {props.noFilename ? "" : ` - ${props.attachment.name}`}
            </span>
        );
    } else {
        title = <span>{props.attachment.name}</span>;
    }

    let deleteIcon: any = null;

    if (!props.isReadOnly) {
        deleteIcon = (
            <OwButton className={"ms-2 btn-danger me-2"} centerIconClass={"fad fa-trash-alt"} fn={() => {
                setSourceElement();
            }}/>
        );
    }

    return (
        <div className={`OwS3DownloadLink position-relative ${props.className || ''}`} id={id}>
            {props.attachment ? (
                <ConfirmDeleteOverlayFn
                    targetElem={targetElem}
                    handleConfirm={deleteAttachment}
                    handleCancel={cancelConfirmDelete}
                    nestedParentElem={id}
                    skipMainView={true}
                />
            ) : null}
            <div className={"d-inline-flex align-items-center secondary-bg-color py-2 px-3 rounded-5 mb-2"} ref={attachmentItemRef}>
                {deleteIcon}{" "}
                <a
                    className={"pointer"}
                    onClick={() => {
                        // downloadAttachment(props.attachment.guid);
                        setRetrievingFile(true);
                    }}>
                    {leftIcon} {title}
                </a>
            </div>
        </div>
    );
};

export default OwS3DownloadLink;
